import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useRevealGroup } from 'hooks/useReveal';
import { useLanguage } from 'hooks/useLanguage';
import './Directions.css';

/**
 * «Основні напрямки роботи СГОСТУ» — ADR-0018.
 *
 * Назви напрямків видно одразу, пояснення розкривається на вимогу.
 * Десктоп: список ліворуч і «сцена» праворуч. Наведення, клік чи фокус обирає
 * пункт: підкладка переїжджає до рядка, номер прокручується лічильником,
 * заголовок виїжджає з-під маски, опис проявляється по словах із розмиття.
 * Поки відвідувач нічого не чіпав, пункти гортаються самі; смуга прогресу під
 * сценою — і є таймер: наступний пункт вмикає її animationend, тож пауза
 * (наведення на сцену, секція поза екраном) зупиняє все разом.
 * Мобільний: акордеон, текст розкривається під назвою, без автогортання.
 *
 * Усі описи лежать в одній клітинці сітки, видимий — лише активний. Висота
 * сцени дорівнює найдовшому тексту, і сторінка під нею не смикається.
 *
 * Формулювання надані власником сайту дослівно.
 */
const DIRECTIONS = [
  {
    ua: {
      title: 'Єдина правова база',
      text: 'Співпраця з українськими танцювальними організаціями для створення єдиних правил і правової бази танцювальних видів спорту.',
    },
    en: {
      title: 'Unified legal framework',
      text: 'Cooperation with Ukrainian dance organizations to create unified rules and a legal framework for dance sports.',
    },
  },
  {
    ua: {
      title: 'Легалізація галузі',
      text: 'Формування прозорих та законних умов роботи спортсменів, тренерів, клубів, суддів, організаторів і проведення змагань.',
    },
    en: {
      title: 'Legalizing the industry',
      text: 'Building transparent and lawful conditions for the work of athletes, coaches, clubs, judges and organizers, and for holding competitions.',
    },
  },
  {
    ua: {
      title: 'Цифровізація та автоматизація',
      text: 'Перехід усіх основних процесів спортивного танцю в сучасний цифровий формат. Впровадження спеціалізованої платформи DanCRM у роботу танцювальних клубів — для автоматизації обліку, комунікації, оплат та управління клубом.',
    },
    en: {
      title: 'Digitalization and automation',
      text: 'Moving all core processes of sports dance into a modern digital format. Introducing DanCRM, a specialized platform, into the work of dance clubs — to automate records, communication, payments and club management.',
    },
  },
  {
    ua: {
      title: 'e-phan — єдина цифрова база',
      text: 'СГОСТУ використовує e-phan — спеціалізовану цифрову систему спортивного танцю, вперше впроваджену в Україні, та офіційного партнера державної платформи Дія.',
    },
    en: {
      title: 'e-phan — a single digital database',
      text: 'SGOSTU uses e-phan — a specialized digital system for sports dance, the first of its kind introduced in Ukraine, and an official partner of the state platform Diia.',
    },
  },
  {
    ua: {
      title: 'Прозорі автоматизовані платежі',
      text: 'Автоматизація членських, реєстраційних та інших платежів у партнерстві з monobank, із прозорим обліком фінансових операцій.',
    },
    en: {
      title: 'Transparent automated payments',
      text: 'Automation of membership, registration and other payments in partnership with monobank, with transparent accounting of financial transactions.',
    },
  },
  {
    ua: {
      title: 'Європейська кар’єра в Україні',
      text: 'СГОСТУ — єдиний представник європейських реєстрів спортивного танцю в Україні. Побудова спортивної та професійної кар’єри в європейській системі ECDS для українських учасників здійснюється через СГОСТУ.',
    },
    en: {
      title: 'A European career in Ukraine',
      text: 'SGOSTU is the only representative of European sports dance registries in Ukraine. Ukrainian participants build their sporting and professional careers in the European ECDS system through SGOSTU.',
    },
  },
];

const MOBILE_QUERY = '(max-width: 900px)';
const pad = (n) => String(n).padStart(2, '0');
const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

export default function Directions() {
  const language = useLanguage();
  const uid = useId();
  const stageId = `${uid}-stage`;
  const count = DIRECTIONS.length;

  // null — на мобільному все згорнуто; сцена тоді показує перший пункт.
  const [active, setActive] = useState(0);
  // Автогортання вимикається назавжди, щойно відвідувач сам обрав пункт.
  const [autoplay, setAutoplay] = useState(
    () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const [inView, setInView] = useState(false);
  const [stageHovered, setStageHovered] = useState(false);
  const [indicator, setIndicator] = useState(null);

  const sectionRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef([]);

  const revealRef = useRevealGroup('.directions-reveal', { step: 70 });
  const shown = active ?? 0;

  const select = useCallback((index, { toggle = false } = {}) => {
    setAutoplay(false);
    setActive((current) => (toggle && current === index ? null : index));
  }, []);

  // Автогортання крутиться, лише коли секцію видно.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.35,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Підкладка активного рядка: позиція й висота з розкладки, рух — transform.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;

    const measure = () => {
      const item = active === null ? null : itemRefs.current[active];
      setIndicator(item ? { y: item.offsetTop, h: item.offsetHeight } : null);
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [active, language]);

  const t = (item) => (language === 'en' ? item.en : item.ua);
  const running = autoplay && inView && !stageHovered;

  return (
    <section className='directions section' ref={sectionRef}>
      <div className='container' ref={revealRef}>
        <div className='section-head directions-head directions-reveal'>
          <span className='section-label'>{language === 'en' ? 'Areas of work' : 'Напрямки діяльності'}</span>
          <h2 className='section-title'>
            {language === 'en' ? 'Main areas of SGOSTU work' : 'Основні напрямки роботи СГОСТУ'}
          </h2>
          <p className='directions-hint'>
            <span className='directions-hint-fine'>
              {language === 'en' ? 'Hover over an area to read more' : 'Наведіть на напрямок, щоб прочитати більше'}
            </span>
            <span className='directions-hint-touch'>
              {language === 'en' ? 'Tap an area to read more' : 'Торкніться напрямку, щоб прочитати більше'}
            </span>
          </p>
        </div>

        <div className='directions-layout'>
          <ol className='directions-list' ref={listRef}>
            {DIRECTIONS.map((item, index) => {
              const open = active === index;
              const panelId = `${uid}-panel-${index}`;
              return (
                <li
                  className='directions-item directions-reveal'
                  key={item.ua.title}
                  ref={(node) => {
                    itemRefs.current[index] = node;
                  }}
                  data-open={open}
                >
                  <button
                    type='button'
                    className='directions-trigger'
                    aria-expanded={open}
                    aria-controls={`${panelId} ${stageId}`}
                    onClick={() => select(index, { toggle: isMobile() })}
                    onPointerEnter={(event) => {
                      if (event.pointerType === 'mouse' && !isMobile()) select(index);
                    }}
                    onFocus={(event) => {
                      if (event.target.matches(':focus-visible') && !isMobile()) select(index);
                    }}
                  >
                    <span className='directions-num tnum'>{pad(index + 1)}</span>
                    <span className='directions-name'>{t(item).title}</span>
                    <span className='directions-icon' aria-hidden='true'>
                      <svg className='directions-icon-arrow' width='18' height='18' viewBox='0 0 24 24'>
                        <path
                          d='M5 12h14M13 6l6 6-6 6'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='1.6'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                      <svg className='directions-icon-plus' width='18' height='18' viewBox='0 0 24 24'>
                        <path d='M5 12h14' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' />
                        <path
                          className='directions-icon-plus-v'
                          d='M12 5v14'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='1.6'
                          strokeLinecap='round'
                        />
                      </svg>
                    </span>
                  </button>

                  <div className='directions-panel' id={panelId}>
                    <div className='directions-panel-inner'>
                      <p className='directions-panel-text'>{t(item).text}</p>
                    </div>
                  </div>
                </li>
              );
            })}

            {/* Після пунктів, щоб не зсувати :nth-child і порядок появи. */}
            <li
              className='directions-indicator'
              aria-hidden='true'
              data-hidden={indicator === null}
              style={indicator ? { transform: `translateY(${indicator.y}px)`, height: indicator.h } : undefined}
            />
          </ol>

          <div
            className='directions-stage directions-reveal'
            id={stageId}
            onPointerEnter={(event) => event.pointerType === 'mouse' && setStageHovered(true)}
            onPointerLeave={() => setStageHovered(false)}
          >
            <div className='directions-stage-core' style={{ '--active': shown }}>
              <div className='directions-counter' aria-hidden='true'>
                <span className='directions-counter-digits tnum'>
                  <span>0</span>
                  <span className='directions-counter-window'>
                    <span className='directions-counter-strip'>
                      {DIRECTIONS.map((item, index) => (
                        <span key={item.ua.title}>{index + 1}</span>
                      ))}
                    </span>
                  </span>
                </span>
                <span className='directions-counter-total tnum'>/ {pad(count)}</span>
              </div>

              <div className='directions-slides'>
                {DIRECTIONS.map((item, index) => (
                  <div className='directions-slide' key={item.ua.title} data-active={index === shown}>
                    <h3 className='directions-slide-title'>
                      <span>{t(item).title}</span>
                    </h3>
                    <span className='directions-slide-rule' aria-hidden='true' />
                    <p className='directions-slide-text'>
                      {t(item)
                        .text.split(' ')
                        .map((word, wordIndex) => (
                          // Порядок слів сталий, індекс — надійний ключ. Пробіл поза
                          // span: у inline-block кінцевий пробіл обрізається.
                          // eslint-disable-next-line react/no-array-index-key
                          <Fragment key={wordIndex}>
                            <span className='directions-word' style={{ '--i': wordIndex }}>
                              {word}
                            </span>{' '}
                          </Fragment>
                        ))}
                    </p>
                  </div>
                ))}
              </div>

              <div className='directions-progress' aria-hidden='true'>
                {DIRECTIONS.map((item, index) => (
                  <span className='directions-progress-seg' key={item.ua.title} data-active={index === shown}>
                    {index === shown && (
                      <span
                        // Новий key перезапускає заповнення на кожному кроці.
                        key={`${shown}-${autoplay}`}
                        className='directions-progress-fill'
                        data-auto={autoplay}
                        data-paused={!running}
                        onAnimationEnd={() => {
                          if (autoplay) setActive((current) => ((current ?? 0) + 1) % count);
                        }}
                      />
                    )}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
